import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification
} from "firebase/auth";
import { Button, Card, Col, Form, Row, Modal } from "react-bootstrap";
import { appFirebase } from "../../firebaseConfig/firebase";
import { useState, useContext, useEffect } from "react";
import { UserContext } from "../../context/UserProvider";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const googleIcon = (
  <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <g>
      <path fill="#4285F4" d="M24 9.5c3.54 0 6.7 1.22 9.19 3.22l6.85-6.85C35.91 2.09 30.28 0 24 0 14.82 0 6.73 5.48 2.69 13.44l7.98 6.2C12.36 13.13 17.74 9.5 24 9.5z"/>
      <path fill="#34A853" d="M46.1 24.55c0-1.64-.15-3.22-.42-4.74H24v9.01h12.42c-.54 2.9-2.18 5.36-4.65 7.01l7.19 5.6C43.98 37.36 46.1 31.45 46.1 24.55z"/>
      <path fill="#FBBC05" d="M9.67 28.65A14.5 14.5 0 0 1 9.5 24c0-1.6.27-3.15.74-4.65l-7.98-6.2A23.93 23.93 0 0 0 0 24c0 3.77.9 7.34 2.49 10.49l8.18-5.84z"/>
      <path fill="#EA4335" d="M24 48c6.28 0 11.54-2.08 15.39-5.66l-7.19-5.6c-2.01 1.35-4.6 2.16-8.2 2.16-6.26 0-11.64-3.63-14.33-8.85l-8.18 5.84C6.73 42.52 14.82 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </g>
  </svg>
);

export const Login = () => {
  const { user, refreshAuth } = useContext(UserContext);
  const auth = getAuth(appFirebase);
  const [error, setError] = useState(null);
  const [isLogin, setIsLogin] = useState(true);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [nombre, setNombre] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user && location.pathname === "/login") {
      navigate('/'); // Redirige al home solo si está en /login
    }
  }, [user, navigate, location]);

  const autenticasion = async (e) => {
    e.preventDefault();
    setError(null);
    const correo = e.target.email.value;
    const password = e.target.password.value;
    const nombreIngresado = isLogin ? null : e.target.nombre?.value || nombre;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(correo)) {
      setError("El formato del correo electrónico no es válido");
      return;
    }

    try {
      if (isLogin) {
        const userCredential = await signInWithEmailAndPassword(auth, correo, password);
        if (!userCredential.user.emailVerified) {
          setError("Debes verificar tu correo electrónico antes de iniciar sesión. Revisa tu bandeja de entrada o spam.");
          await signOut(auth);
          return;
        }
      } else {
        const userCredential = await createUserWithEmailAndPassword(auth, correo, password);
        if (nombreIngresado) {
          await updateProfile(userCredential.user, { displayName: nombreIngresado });
        }
        await sendEmailVerification(userCredential.user);
        MySwal.fire({
          title: 'Verificación de correo',
          text: 'Te enviamos un correo de verificación. Por favor, revisa tu bandeja de entrada o spam antes de iniciar sesión.',
          icon: 'info',
          confirmButtonText: 'Entendido'
        });
        await signOut(auth);
        setIsLogin(true);
        return;
      }
      if (typeof refreshAuth === 'function') {
        await refreshAuth();
      }
    } catch (error) {
      console.error("Error al iniciar sesión:", error);
      setError(isLogin ? "Error al iniciar sesión. Verifica tus credenciales." : "Error al registrar usuario. El correo ya está en uso.");
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      if (typeof refreshAuth === 'function') {
        await refreshAuth();
      }
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
      setError("Error al cerrar sesión");
    }
  };

  const loginWithGoogle = async () => {
    setError(null);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      if (typeof refreshAuth === 'function') {
        await refreshAuth();
      }
    } catch (error) {
      console.error("Error al iniciar sesión con Google:", error);
      setError("Error al iniciar sesión con Google.");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetMessage("");
    setError(null);

    if (!resetEmail) {
      setError("Por favor ingresa tu correo electrónico");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage("Se ha enviado un correo para restablecer tu contraseña");
      if (typeof refreshAuth === 'function') {
        await refreshAuth();
      }
      setTimeout(() => {
        setShowResetModal(false);
        setResetEmail("");
        setResetMessage("");
      }, 3000);
    } catch (error) {
      console.error("Error al enviar correo de recuperación:", error);
      setError("Error al enviar el correo de recuperación. Verifica el correo ingresado.");
    }
  };

  return (
    <Card className="shadow-sm my-3">
      <Card.Body>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        {user ? (
          <Row className="justify-content-end align-items-center">
            <Col className="text-end h-100">{user.email}</Col>
            <Col md={2} className="text-end h-100">
              <Button onClick={handleSignOut}>Salir</Button>
            </Col>
          </Row>
        ) : (
          <>
            <Form onSubmit={autenticasion}>
              {!isLogin && (
                <Form.Control
                  className="mb-3"
                  type="text"
                  placeholder="ingrese su nombre"
                  id="nombre"
                  name="nombre"
                  value={nombre}
                  onChange={e => setNombre(e.target.value)}
                  required
                />
              )}
              <Form.Control
                className="mb-3"
                type="email"
                placeholder="ingrese su correo"
                id="email"
                required
              />
              <Form.Control
                className="mb-3"
                type="password"
                placeholder="ingrese su password"
                id="password"
                required
              />
              <Button type="submit" className="mb-2">
                {isLogin ? "Iniciar Sesión" : "Registrarse"}
              </Button>
              <Button 
                variant="link" 
                onClick={() => setIsLogin(!isLogin)}
                className="d-block"
              >
                {isLogin ? "¿No tienes cuenta? Regístrate" : "¿Ya tienes cuenta? Inicia sesión"}
              </Button>
              {isLogin && (
                <Button 
                  variant="link" 
                  onClick={() => setShowResetModal(true)}
                  className="d-block"
                >
                  ¿Olvidaste tu contraseña?
                </Button>
              )}
            </Form>
            <hr/>
            <Col className="text-center mt-3">
              <Button variant="outline-primary" onClick={loginWithGoogle} style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 auto' }}>
                {googleIcon}
                Iniciar sesión con Google
              </Button>
            </Col>
          </>
        )}
      </Card.Body>

      <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Recuperar Contraseña</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resetMessage ? (
            <div className="alert alert-success">{resetMessage}</div>
          ) : (
            <Form onSubmit={handleResetPassword}>
              <Form.Group className="mb-3">
                <Form.Label>Correo Electrónico</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Ingresa tu correo electrónico"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
              </Form.Group>
              <Button type="submit" variant="primary">
                Enviar correo de recuperación
              </Button>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </Card>
  );
};
